import React, { useState } from "react";
import { supabase } from "./supabase";
import "./App.css";
import lockupStacked from "./assets/brand/lockup-stacked.svg";
import { GOOGLE_AUTH_ENABLED } from "./featureFlags";

export default function Login({ onLogin, onSignUp, onBack }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { data, error: signInError } = await supabase.auth.signInWithPassword(
      {
        email: email.trim().toLowerCase(),
        password,
      },
    );

    if (signInError) {
      setError(signInError.message);
      setLoading(false);
      return;
    }

    // Check if account is disabled via profiles table directly
    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("role")
      .eq("id", data.user.id)
      .maybeSingle();

    // Fail closed only on a genuine read failure — not on "no profile row
    // yet", which is the normal state for a new signup mid-Onboarding.
    if (profileError) {
      await supabase.auth.signOut();
      setError("Couldn't verify your account status. Please try again.");
      setLoading(false);
      return;
    }

    if (profileData?.role === "disabled") {
      await supabase.auth.signOut();
      setError("Your account has been disabled. Please contact your manager.");
      setLoading(false);
      return;
    }

    onLogin(data.user);
    setLoading(false);
  }

  async function handleGoogleLogin() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) setError(error.message);
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        {/* Brand */}
        <div
          className="auth-brand"
          style={{ justifyContent: "center", marginBottom: "28px" }}
        >
          <img
            src={lockupStacked}
            alt="SuqPOS — Smart Sales, Anywhere"
            style={{ height: "110px", width: "auto" }}
          />
        </div>

        <h2 className="auth-title">Welcome back</h2>
        <p className="auth-sub">Sign in to your store</p>

        {error && <div className="auth-error">⚠ {error}</div>}

        <form onSubmit={handleLogin} className="auth-form">
          {GOOGLE_AUTH_ENABLED && (
            <>
              {/* Google Sign In */}
              <button
                type="button"
                className="google-auth-btn"
                onClick={handleGoogleLogin}
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              <div className="auth-divider">
                <span>or</span>
              </div>
            </>
          )}

          <div className="auth-field">
            <label>Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
            />
          </div>
          <div className="auth-field">
            <label>Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? "Signing in..." : "Sign in →"}
          </button>
        </form>

        {onSignUp && (
          <p className="auth-switch">
            Don't have an account?{" "}
            <button className="auth-switch-btn" onClick={onSignUp}>
              Sign up free
            </button>
          </p>
        )}

        {onBack && (
          <p className="auth-switch" style={{ marginTop: "8px" }}>
            <button className="auth-switch-btn" onClick={onBack}>
              ← Back to home
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
